import React from "react";

const SUMMARY_CARD_COUNT = 6;

const DISTRIBUTION_BAR_HEIGHTS = [48, 72, 58, 86, 42, 66, 52, 76, 38];

const OFFICE_BAR_HEIGHTS = [
  88, 72, 64, 78, 56, 69, 48, 62, 44, 54, 81, 68, 59, 74, 51, 65, 46, 57, 41,
  50,
];

const SUMMARY_CARD_KEYS = Array.from(
  {
    length: SUMMARY_CARD_COUNT,
  },
  (_, position) => `summary-card-${position + 1}`
);

const DISTRIBUTION_BARS = DISTRIBUTION_BAR_HEIGHTS.map((height, position) => ({
  id: `distribution-bar-${position + 1}`,
  height,
}));

const OFFICE_BARS = OFFICE_BAR_HEIGHTS.map((height, position) => ({
  id: `office-bar-${position + 1}`,
  height,
}));

const RECENT_ACTIVITY_KEYS = [
  "activity-1",
  "activity-2",
  "activity-3",
  "activity-4",
  "activity-5",
  "activity-6",
];

const RENEWAL_STATUS_KEYS = [
  "not-started",
  "in-progress",
  "submitted",
  "completed",
];

const SummaryCardSkeleton: React.FC = () => {
  return (
    <div
      className="
        flex
        flex-1
        basis-[180px]
        min-w-[180px]
        flex-col
        overflow-hidden
        rounded-xl
        border
        border-[#E7EDF5]
        bg-white
        px-5
        pb-6
        pt-4
        shadow-sm
      "
    >
      <div className="flex w-full items-center justify-between gap-3">
        <div className="h-10 w-10 shrink-0 rounded-xl bg-gray-200" />

        <div className="space-y-1">
          <div className="h-8 w-20 rounded bg-gray-200 min-[1600px]:h-11 min-[1600px]:w-28" />

          <div className="ml-auto h-0.5 w-20 rounded bg-gray-200 min-[1600px]:w-28" />
        </div>
      </div>

      <div className="mt-4 min-w-0 space-y-1 text-left">
        <div className="h-5 w-24 rounded bg-gray-200" />

        <div className="h-3 w-16 rounded bg-gray-200" />
      </div>
    </div>
  );
};

const DistributionChartSkeleton: React.FC = () => {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="h-4 w-44 rounded bg-gray-200" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="flex min-h-[220px] flex-1 items-end gap-3 border-b border-l border-gray-200 px-4 pt-4">
          {DISTRIBUTION_BARS.map((bar) => (
            <div
              key={bar.id}
              className="relative flex h-full min-w-0 flex-1 items-end"
            >
              <div
                className="w-full rounded-t-md bg-gray-200"
                style={{
                  height: `${bar.height}%`,
                }}
              />
            </div>
          ))}
        </div>

        <div className="mt-3 flex justify-between gap-2">
          {DISTRIBUTION_BARS.map((bar) => (
            <div
              key={`label-${bar.id}`}
              className="h-2 min-w-0 flex-1 rounded bg-gray-200"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const RenewalChartSkeleton: React.FC = () => {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="h-4 w-28 rounded bg-gray-200" />

          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-[220px] flex-1 items-center justify-center p-4">
          <div className="relative h-44 w-44 rounded-full bg-gray-200">
            <div className="absolute inset-10 rounded-full bg-white" />
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-100 px-4 py-3">
          <div className="grid grid-cols-4 items-center justify-center gap-3">
            {RENEWAL_STATUS_KEYS.map((status) => (
              <div key={status} className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-gray-200" />

                <div className="h-2.5 w-full rounded bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const OfficeRiskFilterSkeleton: React.FC = () => {
  return (
    <div className="shrink-0 pb-1 pt-2">
      <div className="flex items-center gap-2">
        <div className="h-3 w-24 shrink-0 rounded bg-gray-200" />

        <div className="h-7 min-w-0 flex-1 rounded-md bg-gray-200" />

        <div className="h-3 w-8 shrink-0 rounded bg-gray-200" />

        <div className="h-7 w-20 shrink-0 rounded-full bg-gray-200" />

        <div className="h-7 w-7 shrink-0 rounded-full bg-gray-200" />
      </div>
    </div>
  );
};

const OfficeRiskChartSkeleton: React.FC = () => {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="h-4 w-64 rounded bg-gray-200" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-2 pt-1">
        <OfficeRiskFilterSkeleton />

        <div className="flex min-h-0 flex-1 items-center">
          <div className="w-full">
            <div className="flex min-h-[150px] items-end gap-1 border-b border-l border-gray-200 px-3 pt-3">
              {OFFICE_BARS.map((bar) => (
                <div
                  key={bar.id}
                  className="relative flex h-[150px] min-w-0 flex-1 items-end"
                >
                  <div
                    className="w-full rounded-t-sm bg-gray-200"
                    style={{
                      height: `${bar.height}%`,
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="mt-2 flex justify-between gap-1 px-3">
              {OFFICE_BARS.map((bar) => (
                <div
                  key={`label-${bar.id}`}
                  className="h-1.5 min-w-0 flex-1 rounded bg-gray-200"
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-2 flex shrink-0 items-center justify-center gap-5 border-t border-gray-100 pt-2">
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-gray-200" />

            <div className="h-2.5 w-24 rounded bg-gray-200" />
          </div>

          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-gray-200" />

            <div className="h-2.5 w-28 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    </div>
  );
};

const RecentActivitySkeleton: React.FC = () => {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="h-4 w-28 rounded bg-gray-200" />

          <div className="h-3 w-12 rounded bg-gray-200" />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-3 py-2">
        {RECENT_ACTIVITY_KEYS.map((key, index) => (
          <div
            key={key}
            className={`flex items-start gap-2 px-1 py-1 ${
              index === RECENT_ACTIVITY_KEYS.length - 1
                ? ""
                : "border-b border-gray-100"
            }`}
          >
            <div className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-gray-200" />

            <div className="min-w-0 flex-1 space-y-1">
              <div className="h-2.5 w-full rounded bg-gray-200" />

              <div className="h-2.5 w-4/5 rounded bg-gray-200" />
            </div>

            <div className="mt-0.5 h-2 w-12 shrink-0 rounded bg-gray-200" />
          </div>
        ))}
      </div>
    </div>
  );
};

const BLPDashboardSkeleton: React.FC = () => {
  return (
    <div className="animate-pulse" aria-busy="true">
      <output className="sr-only">
        Loading Business License and Permit dashboard
      </output>

      <div className="mt-4 flex flex-wrap gap-4">
        {SUMMARY_CARD_KEYS.map((key) => (
          <SummaryCardSkeleton key={key} />
        ))}
      </div>

      <div
        className="mt-4 grid gap-4"
        style={{
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        }}
      >
        <DistributionChartSkeleton />

        <RenewalChartSkeleton />
      </div>

      <div
        className="mt-4 grid gap-4"
        style={{
          gridTemplateColumns: "minmax(0, 7fr) minmax(0, 3fr)",
          alignItems: "stretch",
        }}
      >
        <OfficeRiskChartSkeleton />

        <RecentActivitySkeleton />
      </div>
    </div>
  );
};

export default BLPDashboardSkeleton;
